import React, { type ReactElement } from 'react';

import {
	AssignmentStatus,
	type IInventoryItem,
	InventoryItemCondition,
	InventoryListingStatus,
	InventoryMatchingStatus,
	Services,
	type ServicesProviderConfig,
	type ServicesProviderInventory,
	SyncStatus,
	SystemType,
} from '@ekhodealer/ekho-common';

import type {
	InventoryDataSource} from '@ekhodealer/ekho-common';
import type { InventoryDrawerState,InventoryDrawerValidationState, InventoryItem } from './types';

import { PLACEHOLDER } from '@common/lib/strings';

import { DATA_SOURCE_LABELS, FIELD_NAME_TO_LABEL } from './constants';
import { MapPolicy,OnlineSales } from './enums';
import {
	CheckmarkCircleIconFilled,
	ExclamationMarkTriangleIconFilled,
	InfoIconFilled,
	MinusCircleIcon,
} from './icons';

/**
 * Get listing status tag properties for UI display
 */
export const getListingStatusTagProps = (status: InventoryListingStatus): { accent: 3 | 5 | 'neutral'; children: string; indicator: boolean; icon?: ReactElement; iconSide?: 'right' } => {
	
	switch (status) {
		case InventoryListingStatus.PUBLISHED:
			return { accent: 3 as const, children: 'Published', indicator: false }; // Green accent
		case InventoryListingStatus.HIDDEN:
			return { accent: 'neutral' as const, children: 'Hidden', indicator: false }; // Neutral/gray accent
		case InventoryListingStatus.DRAFT:
			return { accent: 5 as const, children: 'Draft', indicator: false }; // Orange accent
		case InventoryListingStatus.INCOMPLETE_DRAFT:
		case InventoryListingStatus.LOCKED:
			return { 
				accent: 5 as const, 
				children: 'Draft',
				indicator: false,
				icon: React.createElement(MinusCircleIcon),
				iconSide: 'right' as const,
			}; // Orange accent (same as Draft) with minus icon on right
		case InventoryListingStatus.ARCHIVED:
			return { accent: 'neutral' as const, children: 'Archived', indicator: false }; // Neutral/gray accent
		default:
			return { accent: 'neutral' as const, children: 'Unknown', indicator: false };
	}
};

/**
 * Get vehicle match tag properties for UI display
 * Maps InventoryMatchingStatus to In progress/Complete display
 */
export const getVehicleMatchTagProps = (status?: InventoryMatchingStatus): { accent: 3 | 5; children: string } => {
	switch (status) {
		case InventoryMatchingStatus.MATCHED:
			return { accent: 3 as const, children: 'Complete' }; // Green accent
		default:
			return { accent: 5 as const, children: 'In progress' }; // Orange accent
	}
};

/**
 * Get fulfillment options label for UI display
 * Returns "All" if both pickup and delivery are present, "Per config" if neither, or individual options
 * When fulfillmentOptions is empty, checks the services provider config to determine available options
 */
export const getFulfillmentOptionsLabel = (
	fulfillmentOptions?: ('delivery' | 'pickup')[],
	servicesProvider?: ServicesProviderInventory | null,
): string => {
	// If fulfillmentOptions is explicitly set, use it
	if (fulfillmentOptions && fulfillmentOptions.length > 0) {
		const hasPickup = fulfillmentOptions.includes('pickup');
		const hasDelivery = fulfillmentOptions.includes('delivery');

		if (hasPickup && hasDelivery) {
			return 'All';
		}

		return hasPickup ? 'Pickup' : 'Delivery';
	}

	// If fulfillmentOptions is empty/null, check services provider config
	if (servicesProvider) {
		// Cast config to ServicesProviderConfig to access delivery and pickup configs
		// ServicesProviderInventory has a restricted type, but the actual config contains all services
		const fullConfig = servicesProvider.config as unknown as ServicesProviderConfig;

		const deliveryConfig = fullConfig?.[Services.DELIVERY];
		const pickupConfig = fullConfig?.[Services.PICKUP];

		const hasDelivery = deliveryConfig?.supported === true;
		const hasPickup = pickupConfig?.supported === true;

		if (hasPickup && hasDelivery) {
			return 'All';
		}

		if (!hasPickup && !hasDelivery) {
			return 'None';
		}

		return hasPickup ? 'Pickup' : 'Delivery';
	}

	return 'None';
};

/**
 * Get sync status icon component based on status
 */
export const getSyncStatusIcon = (status?: SyncStatus): ReactElement | null => {
	switch (status) {
		case SyncStatus.SYNCED:
			return CheckmarkCircleIconFilled();
		case SyncStatus.NEEDS_REVIEW:
			return InfoIconFilled();
		case SyncStatus.FIX_REQUIRED:
			return ExclamationMarkTriangleIconFilled();
		default:
			return null;
	}
};

/**
 * Get sync status properties for UI display
 */
export const getSyncStatusProps = (status?: SyncStatus): { text: string; color: string } => {
	switch (status) {
		case SyncStatus.SYNCED:
			return { text: 'Synced', color: '#00805E' }; // Green
		case SyncStatus.NEEDS_REVIEW:
			return { text: 'Needs review', color: '#D97F18' }; // Orange
		case SyncStatus.FIX_REQUIRED:
			return { text: 'Fix required', color: '#D13636' }; // Red
		case SyncStatus.IN_PROGRESS:
			return { text: 'In progress', color: '#666666' }; // Grey
		default:
			return { text: '--', color: '#666666' };
	}
};

/**
 * Get human-readable vehicle condition from enum value
 */
export const getInventoryItemConditionLabel = (condition: InventoryItemCondition): string => {
	const conditionToLabel: Record<InventoryItemCondition, string> = {
		[InventoryItemCondition.NEW]: 'New',
		[InventoryItemCondition.USED]: 'Used',
		[InventoryItemCondition.CONSIGNED]: 'Consigned',
		[InventoryItemCondition.UNDEFINED]: PLACEHOLDER,
	};
	return conditionToLabel[condition];
};

/**
 * Get data source label from enum
 */
export const getDataSourceLabel = (source?: InventoryDataSource): string => {
	if (!source) return '';
	return DATA_SOURCE_LABELS[source] || '';
};

/**
 * Get color for data source label based on sync status
 */
export const getSourceColor = (syncStatus?: SyncStatus): string => {
	if (!syncStatus) return '#666666'; // grey if no status
	switch (syncStatus) {
		case SyncStatus.SYNCED:
			return '#666666'; // grey
		case SyncStatus.NEEDS_REVIEW:
			return '#D97F18'; // orange
		case SyncStatus.FIX_REQUIRED:
			return '#D13636'; // red
		default:
			return '#666666';
	}
};

/**
 * Build source label with status indicator text
 */
export const getSourceLabelWithStatus = (dataSource?: string, syncStatus?: SyncStatus): string => {
	if (!dataSource) return '';
	if (!syncStatus) return dataSource;
	switch (syncStatus) {
		case SyncStatus.NEEDS_REVIEW:
			return `${dataSource} (stale data)`;
		case SyncStatus.FIX_REQUIRED:
			return `${dataSource} (fix required)`;
		default:
			return dataSource;
	}
};

/**
 * Calculate drawer state changes based on the new MAP policy
 */
export const calculateMapPolicyChanges = (
	state: InventoryDrawerState,
	newMapPolicy: string,
): InventoryDrawerState => {
	const baseState = { ...state, mapPolicy: newMapPolicy };

	// When not subject to MAP policy, retail price should sync with advertised price
	if (newMapPolicy === MapPolicy.NOT_SUBJECT_TO_MAP) {
		return {
			...baseState,
			retailPrice: state.advertisedPrice,
		};
	}

	return baseState;
};

/**
 * These functions are used to get the values for the drawer state from the inventory item
 * and are only used in the buildDrawerStateFromItem function. After the drawer state is built,
 * state is managed by the atoms in the atoms/editDrawerAtoms.ts file.
 */

const getListingStatus = (item: InventoryItem): InventoryListingStatus => {
	return item.listing?.status || InventoryListingStatus.DRAFT;
};

const getCondition = (item: InventoryItem): InventoryItemCondition => {
	return item.newUsed || InventoryItemCondition.UNDEFINED;
};

const getOnlineSalesStatus = (item: InventoryItem): OnlineSales => {
	if (item.newUsed === InventoryItemCondition.USED) {
		return OnlineSales.LEAD_GEN;
	}
	if (item.isMarkedForOnlineSale) {
		return OnlineSales.ENABLED;
	}
	return OnlineSales.LEAD_GEN;
};

const getDealerRetailPrice = (item: InventoryItem): number => {
	return item.pricing?.dealerRetailPrice ?? 0;
};

const getAdvertisedPrice = (item: InventoryItem): number => {
	return item.pricing?.advertisedPrice ?? 0;
};

const getRetailPrice = (item: InventoryItem): number => {
	return item.pricing?.retailPrice ?? 0;
};

const getMapPolicy = (item: InventoryItem): MapPolicy => {
	return item.pricing?.map ? MapPolicy.SUBJECT_TO_MAP : MapPolicy.NOT_SUBJECT_TO_MAP;
};

const getIsMapPolicyDropdownHidden = (item: InventoryItem): boolean => {
	return item.newUsed === InventoryItemCondition.USED;
};

/**
 * Validates dealer retail, advertised price, and retail price
 * Returns validation errors for each field if any
 */
export const getValidationErrorsForPriceFields = (
	dealerRetail: string | number,
	advertisedPrice: string | number,
	retailPrice: string | number,
): InventoryDrawerValidationState => {
	const validateField = (field: keyof InventoryDrawerValidationState, value: string | number): string => {
		const isEmpty = value === undefined || value === null || value === '';
		const numValue = Number(value) || 0;
		const isZero = !isEmpty && numValue === 0;

		if (!isEmpty && !isZero) {
			const dealerRetailNum = Number(dealerRetail) || 0;
			const advertisedPriceNum = Number(advertisedPrice) || 0;
			const retailPriceNum = Number(retailPrice) || 0;

			switch (field) {
				case 'dealerRetail':
					// Dealer retail must be greater than or equal to advertised price
					if (advertisedPriceNum > 0 && numValue < advertisedPriceNum) {
						return 'Dealer retail must be greater than or equal to advertised price';
					}
					break;
				case 'advertisedPrice':
					// Advertised price must be less than or equal to dealer retail
					// and greater than or equal to retail price
					if (dealerRetailNum > 0 && numValue > dealerRetailNum) {
						return 'Advertised price must be less than or equal to dealer retail';
					}
					if (retailPriceNum > 0 && numValue < retailPriceNum) {
						return 'Advertised price must be greater than or equal to retail price';
					}
					break;
				case 'retailPrice':
					// Retail price must be less than or equal to advertised price
					if (advertisedPriceNum > 0 && numValue > advertisedPriceNum) {
						return 'Retail price must be less than or equal to advertised price';
					}
					break;
			}
		}

		if (isEmpty) {
			return `${FIELD_NAME_TO_LABEL[field]} is required`;
		}
		if (isZero) {
			return `${FIELD_NAME_TO_LABEL[field]} must be greater than $0`;
		}

		return '';
	};

	return {
		dealerRetail: validateField('dealerRetail', dealerRetail),
		advertisedPrice: validateField('advertisedPrice', advertisedPrice),
		retailPrice: validateField('retailPrice', retailPrice),
	};
};

/**
 * Builds the drawer state from the inventory item. This is only used to initialize the drawer state
 * when the drawer is opened. After the drawer is opened, the state is managed by the atoms in the
 * atoms/editDrawerAtoms.ts file.
 */
export const buildDrawerStateFromItem = (currentItem: InventoryItem): InventoryDrawerState => {
	const dealerRetail = getDealerRetailPrice(currentItem);
	const advertisedPrice = getAdvertisedPrice(currentItem);
	const rawRetailPrice = getRetailPrice(currentItem);
	const mapPolicy = getMapPolicy(currentItem);

	// When not subject to MAP, retail price should always match advertised price
	const retailPrice =
		mapPolicy === MapPolicy.NOT_SUBJECT_TO_MAP ? advertisedPrice : rawRetailPrice;

	return {
		isDrawerOpen: true,
		selectedItemId: currentItem.id,
		listingStatus: getListingStatus(currentItem),
		condition: getCondition(currentItem),
		onlineSales: getOnlineSalesStatus(currentItem),
		dealerRetail,
		advertisedPrice,
		retailPrice,
		mapPolicy,
		isMapPolicyDropdownHidden: getIsMapPolicyDropdownHidden(currentItem),
		hasUserMadeChanges: false,
		// Store original values for change detection
		originalDealerRetail: dealerRetail,
		originalAdvertisedPrice: advertisedPrice,
		originalRetailPrice: retailPrice,
		cosmeticReport: currentItem.cosmeticReport?.blemishes ?? [],
		cosmeticReportSummary: currentItem.cosmeticReport?.conditionSummary ?? '',
		cosmeticReportEnabled: currentItem.cosmeticReport?.isEnabled ?? true,
		originalCosmeticReport: currentItem.cosmeticReport?.blemishes ?? [],
		originalCosmeticReportSummary: currentItem.cosmeticReport?.conditionSummary ?? '',
		originalCosmeticReportEnabled: currentItem.cosmeticReport?.isEnabled ?? true,
	};
};

/**
 * Build all updates that should be persisted to the backend based on drawer state
 * Includes pricing field values and MAP policy
 */
type BackendInventoryUpdates = {
	isMarkedForOnlineSale: boolean;
	pricing: {
		map: boolean;
		dealerRetailPrice?: number;
		advertisedPrice?: number;
		retailPrice?: number;
	};
	listing?: { status: InventoryListingStatus };
	cosmeticReport?: {
		blemishes: NonNullable<IInventoryItem['cosmeticReport']>['blemishes'];
		conditionSummary: string | undefined;
		isEnabled: boolean;
		updatedAt: number;
	};
};

export const buildBackendUpdates = (state: InventoryDrawerState): BackendInventoryUpdates => {
	// Start with base updates
	const baseUpdates = {
		isMarkedForOnlineSale: state.onlineSales === OnlineSales.ENABLED,
	};

	// Build pricing payload with MAP policy and pricing fields
	const pricingPayload: {
		pricing: {
			map: boolean;
			dealerRetailPrice?: number;
			advertisedPrice?: number;
			retailPrice?: number;
		};
	} = {
		pricing: {
			map: state.mapPolicy === MapPolicy.SUBJECT_TO_MAP,
		},
	};

	// Only include dealer retail if it changed and is valid
	if (
		state.dealerRetail !== state.originalDealerRetail &&
		state.dealerRetail &&
		Number(state.dealerRetail) > 0
	) {
		pricingPayload.pricing.dealerRetailPrice = Number(state.dealerRetail);
	}

	// Only include advertised price if it changed and is valid
	if (
		state.advertisedPrice !== state.originalAdvertisedPrice &&
		state.advertisedPrice &&
		Number(state.advertisedPrice) > 0
	) {
		pricingPayload.pricing.advertisedPrice = Number(state.advertisedPrice);

		// When not subject to MAP, retail price should always match advertised price
		if (state.mapPolicy === MapPolicy.NOT_SUBJECT_TO_MAP) {
			pricingPayload.pricing.retailPrice = Number(state.advertisedPrice);
		}
	}

	// Only include retail price if it changed and is valid (and not already set by MAP logic above)
	if (
		pricingPayload.pricing.retailPrice === undefined &&
		state.retailPrice !== state.originalRetailPrice &&
		state.retailPrice &&
		Number(state.retailPrice) > 0
	) {
		pricingPayload.pricing.retailPrice = Number(state.retailPrice);
	}

	// Build listing updates if needed
	const listingPayload = state.listingStatus
		? {
				listing: {
					status: state.listingStatus,
				},
		  }
		: {};

	// Only include cosmeticReport when blemishes or summary actually changed.
	// Comparing originals prevents stamping updatedAt on every save and ensures
	// the meaningful-updates check in inventoryItemCanSave works correctly.
	const cosmeticReportChanged =
		JSON.stringify(state.cosmeticReport) !==
			JSON.stringify(state.originalCosmeticReport) ||
		state.cosmeticReportSummary !== state.originalCosmeticReportSummary ||
		state.cosmeticReportEnabled !== state.originalCosmeticReportEnabled;

	const cosmeticReportPayload = cosmeticReportChanged
		? {
				cosmeticReport: {
					blemishes: state.cosmeticReport,
					conditionSummary: state.cosmeticReportSummary || undefined,
					isEnabled: state.cosmeticReportEnabled,
					updatedAt: Date.now(),
				},
		  }
		: {};

	// Combine all updates
	return {
		...baseUpdates,
		...pricingPayload,
		...listingPayload,
		...cosmeticReportPayload,
	};
};

/**
 * Check if inventory item can be saved.
 */
export const inventoryItemCanSave = (
	state: InventoryDrawerState,
	validationErrors: InventoryDrawerValidationState,
	ignoreUserMadeChanges: boolean = false,
): boolean => {
	if (!ignoreUserMadeChanges && !state.hasUserMadeChanges) return false;

	const hasValidationErrors = Object.values(validationErrors).some((error) => error);
	if (hasValidationErrors) return false;

	// Check if there are any meaningful updates to save
	const updates = buildBackendUpdates(state);
	// Check if pricing has any fields besides 'map' (dealerRetailPrice, advertisedPrice, or retailPrice)
	const hasPricingFieldUpdates = updates.pricing && Object.keys(updates.pricing).length > 1; // > 1 because 'map' is always included
	const hasListingUpdates = Boolean(updates.listing);
	const hasBaseUpdates = updates.isMarkedForOnlineSale !== undefined;
	// cosmeticReport is only present in updates when blemishes or summary actually changed
	const hasCosmeticUpdates = 'cosmeticReport' in updates;

	return hasPricingFieldUpdates || hasListingUpdates || hasBaseUpdates || hasCosmeticUpdates;
};

/**
 * Get publish blockers for an inventory item
 * Checks for:
 * - At least one image required
 * - Failed detail website scrape (when externalWebsiteId exists)
 * - Assignment status that prevents publishing
 * @param inventoryItem - The inventory item to check for blockers
 * @returns Array of blocker reason strings
 */
export const getPublishBlockers = (inventoryItem: IInventoryItem | null | undefined): string[] => {
	const reasons: string[] = [];

	if (!inventoryItem) {
		return reasons;
	}

	// Check for at least one image
	if (!inventoryItem.imageUrls || inventoryItem.imageUrls.length === 0) {
		reasons.push('At least one image is required');
	}

	// Check for failed detail website scrape when externalWebsiteId exists
	if (!inventoryItem.externalWebsiteId) {
		reasons.push('Website ID is required for redirects');
	}

	// Check assignment status
	if (inventoryItem.assignment?.status) {
		const assignmentStatus = inventoryItem.assignment.status;
		switch (assignmentStatus) {
			case AssignmentStatus.SOFT_LOCKED:
				reasons.push('Temporarily reserved - sales agreement pending');
				break;
			case AssignmentStatus.HARD_LOCKED:
				if (inventoryItem.primarySource?.system === SystemType.LIGHTSPEED) {
					reasons.push('Lightspeed deal is pending finalization');
				} else {
					reasons.push('Item is marked as sold');
				}
				break;
			case AssignmentStatus.SOLD:
				reasons.push('Item is marked as sold');
				break;
			case AssignmentStatus.UNAVAILABLE:
				reasons.push('Item cannot be synced with DMS');
				break;
			case AssignmentStatus.AVAILABLE:
			case AssignmentStatus.UNKNOWN:
				// These statuses don't block publishing
				break;
			default:
				// Handle any future statuses
				break;
		}
	}

	return reasons;
};
